import { Link } from "react-router-dom";
import { ArrowUpRight, AlertTriangle, ShieldCheck } from "lucide-react";

/**
 * Tier limit / approaching-cap banner.
 *
 * Reads a `limit` object produced by `GET /api/billing/tier`:
 *   { tier, cap, current, remaining, over_limit, near_limit, upgrade_to }
 *
 * Renders three states:
 *   - hidden (cap is null OR plenty of headroom)
 *   - "approaching limit" (≥90% used, not over)
 *   - "limit reached" (current ≥ cap)
 *
 * Used both on the Coach Roster page (athletes counter) and on the
 * Settings page (any counter that's tight).
 */
export default function TierLimitBanner({ limit, label = "athletes", tierMeta }) {
  if (!limit || limit.cap == null) return null;
  if (!limit.near_limit && !limit.over_limit) return null;

  const upgradeName = (tierMeta || {})[limit.upgrade_to]?.name || limit.upgrade_to;
  const over = limit.over_limit;
  const Icon = over ? AlertTriangle : ShieldCheck;
  const palette = over
    ? { bg: "rgba(217,4,41,0.10)", border: "rgba(217,4,41,0.45)", fg: "var(--brand-secondary)" }
    : { bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.5)",  fg: "#F59E0B" };

  return (
    <div
      data-testid={`tier-limit-banner-${label}`}
      className="rounded-xl border p-4 flex flex-wrap items-center justify-between gap-3"
      style={{ backgroundColor: palette.bg, borderColor: palette.border }}
    >
      <div className="flex items-start gap-3 min-w-0">
        <Icon className="h-5 w-5 mt-0.5 shrink-0" style={{ color: palette.fg }} />
        <div className="min-w-0">
          <div className="font-bold text-sm text-white">
            {over
              ? `You've reached your tier limit of ${limit.cap} ${label}.`
              : `${limit.current} of ${limit.cap} ${label} used — only ${limit.remaining} left.`}
          </div>
          <div className="text-xs text-white/70 mt-0.5">
            {upgradeName ? (
              <>Upgrade to the <span className="font-semibold text-white">{upgradeName}</span> tier to {over ? "add more " : "unlock unlimited "}{label} and unlock departmental tools.</>
            ) : (
              <>Talk to sales about your Enterprise rollout to lift this cap.</>
            )}
          </div>
        </div>
      </div>
      <Link to={limit.upgrade_to === "enterprise" ? "/enterprise/contact" : "/pricing"}>
        <button
          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-bold text-white red-glow"
          style={{ backgroundColor: palette.fg }}
          data-testid={`tier-limit-banner-cta-${label}`}
        >
          {over ? "Upgrade now" : "Upgrade plan"} <ArrowUpRight className="h-3.5 w-3.5" />
        </button>
      </Link>
    </div>
  );
}
